import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ReportObject } from '../../models/report';
import { Response } from '../../models/user';
import { environment } from '../../../environments/environment.dev';

// API Response interface to match the actual API structure
interface ApiResponse<T> {
  data: T;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  statusCode: number;
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  constructor(private http: HttpClient) {}

  getReportsList(payload: any): Observable<ApiResponse<ReportObject[]>> {
    const { pagination, search, startDate, endDate, reportType, status } =
      payload;

    let params = new HttpParams()
      .set('page', pagination.page.toString())
      .set('limit', pagination.limit.toString());

    // Add search parameter
    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    // Add reportType parameter
    if (reportType && reportType.trim()) {
      params = params.set('reportType', reportType.trim());
    }

    // Add status parameter
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    // Add date range parameters
    if (startDate) {
      params = params.set('from', startDate);
    }

    if (endDate) {
      params = params.set('to', endDate);
    }

    return this.http.get<ApiResponse<ReportObject[]>>(
      environment.endPoints.getReportsList,
      { params },
    );
  }

  downloadReport(
    payload: any,
  ): Observable<Response<{ url: string }>> {
    const body = {
      s3Key: payload.s3Key,
      fileName: payload.reportName,
      email: payload.email,
    };
    const url = `${environment.endPoints.downloadReport}`;
    return this.http.post<Response<{ url: string }>>(url, body);
  }

  shareReport(payload: { email: string; fileName: string }) {
    const url = `${environment.endPoints.shareReport}`;
    return this.http.post<Response<unknown>>(url, payload);
  }

  validateReportLink(code: string): Observable<Response<{ reportName?: string }>> {
    const url = `${environment.endPoints.validateReportLink}/${encodeURIComponent(code)}`;
    return this.http.get<Response<{ reportName?: string }>>(url);
  }

  downloadReportWithCode(payload: { password: string; code: string }): Observable<Response<{ url: string }>> {
    const url = `${environment.endPoints.downloadReportWithCode}`;
    return this.http.post<Response<{ url: string }>>(url, payload);
  }

   deleteReport(reportId: string): Observable<{ statusCode: number }> {
    const url = `${environment.endPoints.deleteReport}/${reportId}`;
    return this.http.get<{ statusCode: number }>(url);
  }

  regenerateReport(reportId: string ): Observable<Response<ReportObject>> {
    const url = `${environment.endPoints.regenerateReport}/${reportId}`;
    return this.http.get<Response<ReportObject>>(url);
  }
}
